import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import StudioLayout from "@/components/layouts/StudioLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus, Search, Pencil, Trash2, Star, Send, Archive, Film, Tv, Sparkles,
  Upload, ListOrdered, ChevronUp, ChevronDown, Calendar as CalendarIcon,
} from "lucide-react";
import { toast } from "sonner";

const KIND_TO_ICON = { movie: Film, series: Tv, reel: Sparkles };

const SAMPLE_CSV = `title,description,video_url,genres,languages,age_rating,release_year,duration_minutes,cast,tags,thumbnail_url
Big Buck Bunny,A giant rabbit takes revenge,https://www.youtube.com/watch?v=aqz-KE-bpKQ,Animation;Comedy,English,U,2008,10,Big Buck Bunny;Frank,blender;short,
Sintel,A lonely young woman befriends a dragon,https://www.youtube.com/watch?v=eRsGyueVLvQ,Animation;Drama,English,U/A 7+,2010,15,Halina Reijn,blender,`;

function statusBadge(status) {
  const map = {
    draft: "bg-slate-500/15 text-slate-300 border-slate-400/30",
    scheduled: "bg-amber-500/15 text-amber-300 border-amber-400/30",
    published: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
    archived: "bg-zinc-500/15 text-zinc-400 border-zinc-400/30",
  };
  return map[status] || map.draft;
}

function monetizationOf(item) {
  const sub = item.subscription_included !== false;
  const has_tvod = !!(item.tvod_rent_price || item.tvod_buy_price);
  if (sub && has_tvod) return { label: "Sub + TVOD", className: "border-violet-400/40 text-violet-200 bg-violet-500/10" };
  if (sub) return { label: "Sub", className: "border-indigo-400/40 text-indigo-200 bg-indigo-500/10" };
  if (has_tvod) return { label: "TVOD", className: "border-amber-400/40 text-amber-200 bg-amber-500/10" };
  return { label: "Locked", className: "border-rose-400/40 text-rose-200 bg-rose-500/10" };
}

export default function StudioCatalogue() {
  const [tenant, setTenant] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");
  const [monFilter, setMonFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [csvOpen, setCsvOpen] = useState(false);
  const [reorderOpen, setReorderOpen] = useState(false);

  const refresh = async () => {
    try {
      const [t, c] = await Promise.all([api.get("/tenant/me"), api.get("/cms/content")]);
      setTenant(t.data);
      setItems(c.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    let list = items;
    if (statusFilter !== "all") list = list.filter((i) => i.status === statusFilter);
    if (kindFilter !== "all") list = list.filter((i) => i.kind === kindFilter);
    if (monFilter !== "all") {
      list = list.filter((i) => {
        const m = monetizationOf(i).label.toLowerCase().replace(" + ", "_").replace(" ", "_");
        if (monFilter === "sub") return m === "sub";
        if (monFilter === "tvod") return m === "tvod";
        if (monFilter === "sub_tvod") return m === "sub_tvod";
        if (monFilter === "locked") return m === "locked";
        return true;
      });
    }
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((i) =>
      i.title.toLowerCase().includes(q) ||
      (i.tags || []).some((t) => t.toLowerCase().includes(q)) ||
      (i.cast || []).some((c) => c.toLowerCase().includes(q)),
    );
    return list;
  }, [items, statusFilter, kindFilter, monFilter, search]);

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const onAction = async (item, action) => {
    try {
      if (action === "delete") {
        if (!window.confirm(`Delete "${item.title}"? This permanently removes episodes/watchlists/progress.`)) return;
        await api.delete(`/cms/content/${item.id}`);
        toast.success("Deleted");
      } else if (action === "publish") {
        await api.post(`/cms/content/${item.id}/publish`);
        toast.success("Published");
      } else if (action === "archive") {
        await api.patch(`/cms/content/${item.id}/archive`);
        toast.success("Archived");
      } else if (action === "feature") {
        await api.post(`/cms/content/${item.id}/feature`);
        toast.success(item.featured ? "Removed from featured" : "Added to featured");
      }
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const bulkAction = async (action) => {
    if (selected.size === 0) {
      toast.error("Select at least one row");
      return;
    }
    const ids = Array.from(selected);
    try {
      for (const id of ids) {
        if (action === "publish") await api.post(`/cms/content/${id}/publish`);
        else if (action === "archive") await api.patch(`/cms/content/${id}/archive`);
      }
      toast.success(`${ids.length} item(s) ${action === "publish" ? "published" : "archived"}`);
      setSelected(new Set());
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const featuredItems = useMemo(() => items.filter((i) => i.featured && i.status === "published"), [items]);

  return (
    <StudioLayout tenant={tenant}>
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.4em] text-white/40">Studio admin · Catalogue</p>
          <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl tracking-tight">Your titles</h1>
          <p className="text-white/50 mt-1 text-sm font-light">Bandwidth is on YouTube/Vimeo — your subscribers stream for free.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            className="rounded-none h-10 text-white/70 hover:text-white border border-white/15 px-3 uppercase text-[11px] tracking-[0.25em]"
            onClick={() => setReorderOpen(true)}
            data-testid="catalogue-reorder-button"
          >
            <ListOrdered className="w-4 h-4 mr-1.5" /> Reorder featured
          </Button>
          <Button
            variant="ghost"
            className="rounded-none h-10 text-white/70 hover:text-white border border-white/15 px-3 uppercase text-[11px] tracking-[0.25em]"
            onClick={() => setCsvOpen(true)}
            data-testid="catalogue-bulk-import-button"
          >
            <Upload className="w-4 h-4 mr-1.5" /> Bulk import (CSV)
          </Button>
          <Link
            to="/studio/catalogue/new"
            className="rounded-none bg-white text-black hover:bg-amber-300 uppercase text-[11px] tracking-[0.25em] h-10 inline-flex items-center px-4"
            data-testid="catalogue-new-button"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New title
          </Link>
        </div>
      </div>

      {/* Filter chips row */}
      <div className="flex flex-col gap-3 mb-4">
        <ChipGroup label="Status" value={statusFilter} setValue={setStatusFilter} testidPrefix="status"
          options={[
            { id: "all", label: "All" },
            { id: "draft", label: "Drafts" },
            { id: "scheduled", label: "Scheduled" },
            { id: "published", label: "Published" },
            { id: "archived", label: "Archived" },
          ]}
        />
        <ChipGroup label="Kind" value={kindFilter} setValue={setKindFilter} testidPrefix="kind"
          options={[
            { id: "all", label: "All" },
            { id: "movie", label: "Movies" },
            { id: "series", label: "Series" },
            { id: "reel", label: "Reels" },
          ]}
        />
        <ChipGroup label="Monetization" value={monFilter} setValue={setMonFilter} testidPrefix="monetization"
          options={[
            { id: "all", label: "All" },
            { id: "sub", label: "Sub" },
            { id: "tvod", label: "TVOD" },
            { id: "sub_tvod", label: "Sub + TVOD" },
            { id: "locked", label: "Locked" },
          ]}
        />
      </div>

      {/* Bulk action bar */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="text-[11px] uppercase tracking-[0.25em] text-white/40">
          {selected.size > 0 ? `${selected.size} selected` : `${filtered.length} title${filtered.length === 1 ? "" : "s"}`}
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {selected.size > 0 && (
            <>
              <Button
                variant="ghost"
                className="rounded-none h-9 text-emerald-300 hover:text-emerald-200 border border-emerald-400/20 px-3 uppercase text-[11px] tracking-[0.25em]"
                onClick={() => bulkAction("publish")}
                data-testid="bulk-publish-button"
              >
                <Send className="w-3.5 h-3.5 mr-1.5" /> Publish
              </Button>
              <Button
                variant="ghost"
                className="rounded-none h-9 text-zinc-300 hover:text-zinc-200 border border-zinc-400/20 px-3 uppercase text-[11px] tracking-[0.25em]"
                onClick={() => bulkAction("archive")}
                data-testid="bulk-archive-button"
              >
                <Archive className="w-3.5 h-3.5 mr-1.5" /> Archive
              </Button>
            </>
          )}
          <div className="relative w-full md:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              placeholder="Search title, tag, cast"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-transparent border-white/15 rounded-none h-9 focus-visible:border-white"
              data-testid="catalogue-search"
            />
          </div>
        </div>
      </div>

      {/* Custom table */}
      <div className="border border-white/10 bg-[#121212] overflow-x-auto" data-testid="catalogue-table-wrap">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-[10px] uppercase tracking-[0.25em] text-white/40">
              <th className="px-3 py-3 w-8"></th>
              <th className="px-3 py-3 text-left">Title</th>
              <th className="px-3 py-3 text-left">Kind</th>
              <th className="px-3 py-3 text-left">Status</th>
              <th className="px-3 py-3 text-left">Monetization</th>
              <th className="px-3 py-3 text-left">Updated</th>
              <th className="px-3 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="catalogue-table-body">
            {loading ? (
              <tr><td colSpan={7} className="text-center text-white/40 py-12 text-xs uppercase tracking-widest">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-white/40 py-12 text-xs uppercase tracking-widest">
                No content matches. <Link to="/studio/catalogue/new" className="text-white underline decoration-white/30 underline-offset-4">Add your first title.</Link>
              </td></tr>
            ) : (
              filtered.map((it) => {
                const Icon = KIND_TO_ICON[it.kind] || Film;
                const mon = monetizationOf(it);
                const isScheduled = it.status === "scheduled";
                return (
                  <tr key={it.id} className="border-b border-white/5 hover:bg-white/[0.025]" data-testid={`catalogue-row-${it.slug}`}>
                    <td className="px-3 py-3 align-middle">
                      <Checkbox
                        checked={selected.has(it.id)}
                        onCheckedChange={() => toggleSelect(it.id)}
                        className="rounded-none border-white/30 data-[state=checked]:bg-white data-[state=checked]:text-black"
                        data-testid={`catalogue-select-${it.slug}`}
                      />
                    </td>
                    <td className="px-3 py-3">
                      <Link to={`/studio/catalogue/${it.id}`} className="flex items-center gap-3 group">
                        <div className="w-16 h-9 bg-black/40 border border-white/10 overflow-hidden flex-shrink-0">
                          {it.thumbnail_url ? (
                            <img src={it.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                          ) : null}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate group-hover:text-amber-300 max-w-[280px]">{it.title}</div>
                          <div className="text-[11px] text-white/40 font-mono truncate max-w-[280px]">{it.slug}</div>
                        </div>
                      </Link>
                    </td>
                    <td className="px-3 py-3">
                      <span className="inline-flex items-center gap-1.5 text-xs text-white/70">
                        <Icon className="w-3.5 h-3.5" /> {it.kind}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 border text-[10px] uppercase tracking-[0.25em] ${statusBadge(it.status)}`}>
                        {isScheduled && <CalendarIcon className="w-3 h-3" />}
                        {it.status}
                      </span>
                      {it.featured && <Star className="ml-2 inline-block w-3.5 h-3.5 text-amber-400 fill-amber-400" />}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 border text-[10px] uppercase tracking-[0.25em] ${mon.className}`}>
                        {mon.label}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-white/55 text-xs">
                      {it.updated_at ? new Date(it.updated_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-white/60 hover:text-amber-300" onClick={() => onAction(it, "feature")} title="Toggle featured" data-testid={`catalogue-feature-${it.slug}`}>
                          <Star className="w-4 h-4" />
                        </Button>
                        {it.status === "published" ? (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-300 hover:text-zinc-200" onClick={() => onAction(it, "archive")} title="Archive" data-testid={`catalogue-archive-${it.slug}`}>
                            <Archive className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-300 hover:text-emerald-200" onClick={() => onAction(it, "publish")} title="Publish" data-testid={`catalogue-publish-${it.slug}`}>
                            <Send className="w-4 h-4" />
                          </Button>
                        )}
                        <Link
                          to={`/studio/catalogue/${it.id}`}
                          className="grid place-items-center h-8 w-8 text-white/60 hover:text-white"
                          data-testid={`catalogue-edit-${it.slug}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Link>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-300 hover:text-rose-200" onClick={() => onAction(it, "delete")} title="Delete" data-testid={`catalogue-delete-${it.slug}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <BulkImportDialog open={csvOpen} onOpenChange={setCsvOpen} onDone={refresh} />
      <ReorderFeaturedDialog open={reorderOpen} onOpenChange={setReorderOpen} items={featuredItems} onDone={refresh} />
    </StudioLayout>
  );
}

function ChipGroup({ label, value, setValue, options, testidPrefix }) {
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`chipgroup-${testidPrefix}`}>
      <span className="text-[10px] uppercase tracking-[0.3em] text-white/40 mr-1">{label}</span>
      {options.map((o) => {
        const active = value === o.id;
        return (
          <button
            key={o.id}
            onClick={() => setValue(o.id)}
            className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] border transition-colors ${active ? "bg-white text-black border-white" : "border-white/15 text-white/55 hover:border-white/40 hover:text-white"}`}
            data-testid={`chip-${testidPrefix}-${o.id}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function BulkImportDialog({ open, onOpenChange, onDone }) {
  const [csv, setCsv] = useState("");
  const [kind, setKind] = useState("movie");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!csv.trim()) {
      toast.error("Paste CSV first");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/cms/content/bulk-import", { csv_text: csv, kind });
      setResult(data);
      toast.success(`Imported ${data.created} item(s)`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-2xl rounded-none" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Bulk import (CSV)</DialogTitle>
          <DialogDescription className="text-white/55">
            Paste CSV with header row. Required: <span className="font-mono text-white/80">title, video_url</span>. Multi-value columns use <span className="font-mono">;</span> separator.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">Kind</span>
            {["movie", "reel"].map((k) => (
              <button
                key={k}
                onClick={() => setKind(k)}
                className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] border ${kind === k ? "bg-white text-black border-white" : "border-white/15 text-white/55 hover:border-white/40"}`}
                data-testid={`bulk-kind-${k}`}
              >
                {k}
              </button>
            ))}
            <button
              onClick={() => setCsv(SAMPLE_CSV)}
              className="ml-auto text-[10px] uppercase tracking-[0.25em] text-white/55 hover:text-white"
              data-testid="bulk-load-sample"
            >
              Load sample
            </button>
          </div>
          <textarea
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            rows={10}
            className="w-full bg-black/40 border border-white/15 p-3 text-xs font-mono focus:outline-none focus:border-white"
            placeholder="title,description,video_url,..."
            data-testid="bulk-csv-textarea"
          />
          {result && (
            <div className="border border-white/10 bg-black/30 p-3 text-xs space-y-1" data-testid="bulk-result">
              <div className="text-emerald-300">Created: {result.created}</div>
              {result.errors.length > 0 && (
                <div className="text-rose-300">Errors: {result.errors.length}</div>
              )}
              {result.errors.slice(0, 5).map((e, i) => (
                <div key={i} className="text-white/60">Row {e.row}: {e.title} — {e.error}</div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none uppercase text-[11px] tracking-[0.25em]">Close</Button>
          <Button onClick={submit} disabled={busy} className="rounded-none bg-white text-black hover:bg-amber-300 uppercase text-[11px] tracking-[0.25em]" data-testid="bulk-submit-button">
            {busy ? "Importing…" : "Import as drafts"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReorderFeaturedDialog({ open, onOpenChange, items, onDone }) {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setList(items.map((i) => ({ id: i.id, title: i.title, slug: i.slug, thumbnail_url: i.thumbnail_url })));
  }, [items, open]);

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[idx], next[j]] = [next[j], next[idx]];
    setList(next);
  };

  const save = async () => {
    if (list.length === 0) {
      toast.error("Nothing to reorder");
      return;
    }
    setBusy(true);
    try {
      await api.post("/cms/featured/reorder", { ordered_ids: list.map((i) => i.id) });
      toast.success("Hero order saved");
      onDone();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-lg rounded-none" data-testid="reorder-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Reorder featured</DialogTitle>
          <DialogDescription className="text-white/55">
            Top to bottom = order they appear in the hero carousel.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[55vh] overflow-y-auto" data-testid="reorder-list">
          {list.length === 0 ? (
            <div className="border border-dashed border-white/10 p-8 text-center text-white/45 text-sm">
              No featured titles yet. Star a published title first.
            </div>
          ) : list.map((it, idx) => (
            <div key={it.id} className="flex items-center gap-3 p-3 border border-white/10" data-testid={`reorder-item-${it.slug}`}>
              <div className="font-mono text-xs text-white/40 w-6">{String(idx + 1).padStart(2, "0")}</div>
              <div className="w-14 h-8 bg-black/40 border border-white/10 overflow-hidden flex-shrink-0">
                {it.thumbnail_url && <img src={it.thumbnail_url} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 text-sm truncate">{it.title}</div>
              <div className="flex flex-col gap-1">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-white/60 hover:text-white disabled:opacity-30" data-testid={`reorder-up-${it.slug}`}>
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button onClick={() => move(idx, 1)} disabled={idx === list.length - 1} className="text-white/60 hover:text-white disabled:opacity-30" data-testid={`reorder-down-${it.slug}`}>
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none uppercase text-[11px] tracking-[0.25em]">Cancel</Button>
          <Button onClick={save} disabled={busy || list.length === 0} className="rounded-none bg-white text-black hover:bg-amber-300 uppercase text-[11px] tracking-[0.25em]" data-testid="reorder-save-button">
            {busy ? "Saving…" : "Save order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
